// Tabella dati responsive con supporto ordinamento e selezione
import { cn } from '../../lib/utils'
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react'

interface Column<T> {
  key: string
  header: string
  cell: (row: T, index: number) => React.ReactNode
  className?: string
  sortable?: boolean
}

interface TableProps<T> {
  data: T[]
  columns: Column<T>[]
  keyExtractor: (row: T, index: number) => string
  isLoading?: boolean
  onRowClick?: (row: T) => void
  sortKey?: string
  sortOrder?: 'asc' | 'desc'
  onSort?: (key: string) => void
  emptyMessage?: string
  className?: string
  rowClassName?: (row: T) => string
}

export function Table<T>({
  data,
  columns,
  keyExtractor,
  isLoading,
  onRowClick,
  sortKey,
  sortOrder,
  onSort,
  emptyMessage = 'Nessun dato disponibile',
  className,
  rowClassName,
}: TableProps<T>) {
  return (
    <div className={cn('overflow-x-auto rounded-lg border border-[var(--fc-border)]', className)}>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[var(--fc-border)] bg-[var(--fc-surface-raised)]">
            {columns.map((col) => (
              <th
                key={col.key}
                className={cn(
                  'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-[var(--fc-foreground-muted)]',
                  col.sortable && 'cursor-pointer select-none hover:text-[var(--fc-foreground)]',
                  col.className
                )}
                onClick={col.sortable && onSort ? () => onSort(col.key) : undefined}
                aria-sort={
                  col.sortable && sortKey === col.key
                    ? sortOrder === 'asc' ? 'ascending' : 'descending'
                    : undefined
                }
              >
                <span className="flex items-center gap-1">
                  {col.header}
                  {col.sortable && (
                    <span className="text-[var(--fc-foreground-subtle)]">
                      {sortKey === col.key ? (
                        sortOrder === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />
                      ) : (
                        <ChevronsUpDown className="h-3 w-3" />
                      )}
                    </span>
                  )}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <tr key={i} className="border-b border-[var(--fc-border)] last:border-0">
                {columns.map((col) => (
                  <td key={col.key} className="px-4 py-3">
                    <div className="h-4 animate-pulse rounded bg-[var(--fc-gray-200)]" />
                  </td>
                ))}
              </tr>
            ))
          ) : data.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                className="px-4 py-12 text-center text-[var(--fc-foreground-muted)]"
              >
                {emptyMessage}
              </td>
            </tr>
          ) : (
            data.map((row, index) => (
              <tr
                key={keyExtractor(row, index)}
                className={cn(
                  'border-b border-[var(--fc-border)] last:border-0 bg-[var(--fc-surface)] transition-colors',
                  onRowClick && 'cursor-pointer hover:bg-[var(--fc-surface-raised)]',
                  rowClassName?.(row)
                )}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={cn('px-4 py-3 text-[var(--fc-foreground)]', col.className)}
                  >
                    {col.cell(row, index)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  )
}
